// Dynamic menu loader - fetches from GitHub repository
// Data source: https://github.com/jonahkleinman/bc-data

import { getSchoolConfig, resolveSchoolId } from '@/config/schools';

const menuCache = new Map();

function getMenuCacheKey(yearMonth, schoolId) {
  const school = getSchoolConfig(schoolId);
  const branch = school.menuGithubBranch || 'main';
  return `${schoolId}:${school.menuGithubRepo}:${branch}:${yearMonth}`;
}

function getMenuDataBaseUrl(schoolId) {
  const school = getSchoolConfig(schoolId);
  const branch = school.menuGithubBranch || 'main';
  return `https://raw.githubusercontent.com/${school.menuGithubRepo}/${branch}/data`;
}

/**
 * Load menu data for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string|null} schoolId - Optional school ID override
 * @returns {Promise<object|null>} Menu data for the date, or null if not found
 */
export async function getMenuForDate(date, schoolId = null) {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return null;
  }

  const resolvedSchoolId = resolveSchoolId({ schoolId });
  const yearMonth = date.substring(0, 7); // "2026-01"
  const cacheKey = getMenuCacheKey(yearMonth, resolvedSchoolId);
  const dataBaseUrl = getMenuDataBaseUrl(resolvedSchoolId);

  // Check cache first
  if (menuCache.has(cacheKey)) {
    const monthData = menuCache.get(cacheKey);
    return monthData[date] || null;
  }

  // Fetch the month's data from GitHub
  try {
    const response = await fetch(`${dataBaseUrl}/${yearMonth}.json`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const monthData = await response.json();

    // Cache it
    menuCache.set(cacheKey, monthData);

    return monthData[date] || null;
  } catch (error) {
    console.warn(`No menu data found for ${resolvedSchoolId} ${yearMonth}:`, error.message);
    return null;
  }
}

/**
 * Check if menu exists for a date (without loading it)
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string|null} schoolId - Optional school ID override
 * @returns {Promise<boolean>}
 */
export async function hasMenuForDate(date, schoolId = null) {
  const data = await getMenuForDate(date, schoolId);
  return data !== null;
}

/**
 * Preload a month's worth of menu data
 * @param {string} yearMonth - Year-month in YYYY-MM format
 * @param {string|null} schoolId - Optional school ID override
 */
export async function preloadMonth(yearMonth, schoolId = null) {
  const resolvedSchoolId = resolveSchoolId({ schoolId });
  const cacheKey = getMenuCacheKey(yearMonth, resolvedSchoolId);

  if (menuCache.has(cacheKey)) {
    return;
  }

  try {
    const response = await fetch(`${getMenuDataBaseUrl(resolvedSchoolId)}/${yearMonth}.json`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const monthData = await response.json();
    menuCache.set(cacheKey, monthData);
  } catch (error) {
    console.warn(`Failed to preload ${resolvedSchoolId} ${yearMonth}:`, error.message);
  }
}
